import PeopleCountingDashboard from './components/PeopleCountingDashboard';
import PeopleCountingInstellingen from './components/PeopleCountingInstellingen';
import KentekenInstellingen from './components/KentekenInstellingen';
import MijneticketsDashboard from './components/MijneticketsDashboard';
import OverzichtDashboard from './components/OverzichtDashboard';
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  Car, Activity, Plus, Search,
  LogOut, Menu, X, BarChart3, FileText,
  CheckCircle, AlertCircle, Loader, Users, Edit2, Trash2, Settings, Sun, Moon, Camera, ChevronDown, Ticket, Calendar, LockOpen,
  Shield, Copy, Eye, EyeOff
} from 'lucide-react';

// Import Supabase client
import { supabase, authService, vehicleService, statsService, userService, scanService, auditService, securityService, appSettingsService, ALL_MAIN_PAGES, DEFAULT_ALLOWED_PAGES, hasPageAccess } from './lib/supabaseClient';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notification, setNotification] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage or default to 'dark'
    return localStorage.getItem('zf-theme') || 'dark';
  });
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [visitedPages, setVisitedPages] = useState(new Set(['dashboard']));
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaStep, setMfaStep] = useState(null); // 'verify' | 'enroll'
  const [mfaData, setMfaData] = useState({});
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaShowSecret, setMfaShowSecret] = useState(false);
  const mfaFlowActiveRef = React.useRef(false);
  const microsoftLoginLoggedRef = React.useRef(false);
  const userProfileRef = React.useRef(null);

  // Theme classes
  const themeClasses = {
    dark: {
      bg: 'bg-zf-dark',
      bgSecondary: 'bg-zf-darker',
      text: 'text-white',
      textSecondary: 'text-gray-300',
      textMuted: 'text-gray-400',
      border: 'border-gray-700',
      input: 'bg-zf-dark border-gray-600 text-white placeholder-gray-500',
      card: 'bg-zf-darker border-gray-700',
      hover: 'hover:bg-zf-dark/50',
      navActive: 'bg-zf-dark',
    },
    light: {
      bg: 'bg-gray-100',
      bgSecondary: 'bg-white',
      text: 'text-gray-900',
      textSecondary: 'text-gray-700',
      textMuted: 'text-gray-500',
      border: 'border-gray-200',
      input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
      card: 'bg-white border-gray-200',
      hover: 'hover:bg-gray-50',
      navActive: 'bg-gray-200',
    }
  };

  const t = themeClasses[theme];

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('zf-theme', theme);
  }, [theme]);

  // Keep ref in sync so the auth subscription closure ([] deps) can see current userProfile
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // Track visited pages to keep them mounted
  useEffect(() => {
    setVisitedPages(prev => {
      if (prev.has(currentPage)) return prev;
      return new Set([...prev, currentPage]);
    });
  }, [currentPage]);

  // Redirect naar eerste toegestane pagina als huidige pagina niet toegankelijk is
  useEffect(() => {
    if (!userProfile || userProfile.role === 'admin') return;
    const adminOnlyPages = ['users', 'settings_general', 'settings_kentekens', 'settings_camera'];
    if (adminOnlyPages.includes(currentPage)) return;
    const allowedPages = userProfile.allowed_pages || DEFAULT_ALLOWED_PAGES;
    if (!allowedPages.includes(currentPage)) {
      const firstAccessible = allowedPages[0] || 'dashboard';
      setCurrentPage(firstAccessible);
    }
  }, [userProfile, currentPage]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const startMfaFlow = React.useCallback(async () => {
    try {
      const factors = await authService.getMfaFactors();
      const totpFactor = factors?.totp?.find(f => f.status === 'verified');
      if (totpFactor) {
        const challenge = await authService.challengeMfa(totpFactor.id);
        setMfaStep('verify');
        setMfaData({ factorId: totpFactor.id, challengeId: challenge.id });
      } else {
        const enrollment = await authService.enrollMfa();
        const qrCode = enrollment.totp?.qr_code ?? enrollment.qr_code ?? null;
        const secret = enrollment.totp?.secret ?? enrollment.secret ?? null;
        setMfaStep('enroll');
        setMfaData({ factorId: enrollment.id, qrCode, secret });
      }
    } catch (error) {
      console.error('MFA flow fout:', error.message);
      setMfaStep('error');
    }
  }, []);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        try {
          const isMicrosoftLogin = session.user.app_metadata?.provider !== 'email' && (
            session.user.app_metadata?.provider === 'azure' ||
            session.user.app_metadata?.providers?.includes('azure') ||
            session.user.identities?.some(i => i.provider === 'azure')
          );

          if (isMicrosoftLogin) {
            let profile = null;
            try { profile = await userService.getUserProfile(session.user.id); } catch (err) { /* nog niet aangemaakt */ }
            if (!profile) {
              await supabase.from('user_profiles').insert([{
                id: session.user.id,
                username: session.user.email?.split('@')[0] || session.user.id,
                role: 'gebruiker',
                active: true,
                allowed_pages: ['kenteken_scans']
              }]);
              profile = await userService.getUserProfile(session.user.id);
            }
            setUserProfile(profile);
            if (!microsoftLoginLoggedRef.current) {
              microsoftLoginLoggedRef.current = true;
              securityService.logLoginPoging({ gebruikersnaam: session.user.email, geslaagd: true, actie: 'microsoft_login' });
            }
          } else {
            const factors = await authService.getMfaFactors();
            const totpFactor = factors?.totp?.find(f => f.status === 'verified');
            const aalData = await authService.getMfaLevel();
            const needsMfa = !totpFactor || (aalData?.currentLevel !== 'aal2');
            if (needsMfa) {
              setMfaRequired(true);
              mfaFlowActiveRef.current = true;
              await startMfaFlow();
            } else {
              const profile = await userService.getUserProfile(session.user.id);
              setUserProfile(profile);
            }
          }
        } catch (error) {
          console.error('Error bij sessie laden:', error);
        }
      }
      setLoading(false);
    }).catch(err => {
      console.error('getSession mislukt:', err);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'SIGNED_IN') {
        let isSameUser = false;
        setUser(prevUser => {
          if (prevUser?.id === session?.user?.id) { isSameUser = true; return prevUser; }
          return session?.user ?? null;
        });

        if (session?.user && !mfaFlowActiveRef.current) {
          // Token refresh can fire SIGNED_IN for the same user — skip the full reload
          // to avoid a window where user is set but userProfile is null (stuck loading).
          if (isSameUser && userProfileRef.current) {
            setLoading(false);
            return;
          }
          try {
            const isMicrosoftLogin = session.user.app_metadata?.provider !== 'email' && (
              session.user.app_metadata?.provider === 'azure' ||
              session.user.app_metadata?.providers?.includes('azure') ||
              session.user.identities?.some(i => i.provider === 'azure')
            );
            // Bij Microsoft login: profiel aanmaken als het nog niet bestaat
            if (isMicrosoftLogin) {
              let profile = null;
              try { profile = await userService.getUserProfile(session.user.id); } catch (err) { /* nieuw account */ }
              if (!profile) {
                await supabase.from('user_profiles').insert([{
                  id: session.user.id,
                  username: session.user.email?.split('@')[0] || session.user.id,
                  role: 'gebruiker',
                  active: true,
                  allowed_pages: ['kenteken_scans']
                }]);
                profile = await userService.getUserProfile(session.user.id);
              }
              setUserProfile(profile);
              // Voorkom dubbele logging: getSession en onAuthStateChange kunnen beide vuren
              if (!microsoftLoginLoggedRef.current) {
                microsoftLoginLoggedRef.current = true;
                securityService.logLoginPoging({ gebruikersnaam: session.user.email, geslaagd: true, actie: 'microsoft_login' });
              }
              setLoading(false);
              return;
            }

            // Lokale gebruiker: MFA altijd verplicht
            const factors = await authService.getMfaFactors();
            const totpFactor = factors?.totp?.find(f => f.status === 'verified');
            const aalData = await authService.getMfaLevel();
            const needsMfa = !totpFactor || (aalData?.currentLevel !== 'aal2');
            if (needsMfa) {
              setMfaRequired(true);
              mfaFlowActiveRef.current = true;
              await startMfaFlow();
            } else {
              setMfaRequired(false);
              const profile = await userService.getUserProfile(session.user.id);
              setUserProfile(profile);
            }
          } catch (error) {
            console.error('Login flow fout:', error.message);
            // Prevent stuck loading: if profile load fails, clear user so login page is shown
            setUser(null);
            setUserProfile(null);
          }
          setLoading(false);
        }
        return;
      }

      if (event === 'SIGNED_OUT') {
        setLoading(true);
        setUser(null);
        setUserProfile(null);
        setMfaRequired(false);
        setMfaStep(null);
        setMfaData({});
        setMfaCode('');
        setMfaError('');
        mfaFlowActiveRef.current = false;
        microsoftLoginLoggedRef.current = false;
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      showNotification('Uitgelogd', 'success');
    } catch (error) {
      showNotification('Fout bij uitloggen: ' + error.message, 'error');
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError('');
    try {
      let factorId = mfaData.factorId;
      let challengeId = mfaData.challengeId;

      if (mfaStep === 'enroll') {
        const challenge = await authService.challengeMfa(factorId);
        challengeId = challenge.id;
      }

      await authService.verifyMfa(factorId, challengeId, mfaCode.trim());

      const profile = await userService.getUserProfile(user.id);
      setUserProfile(profile);
      securityService.logLoginPoging({
        gebruikersnaam: profile?.username || user.email,
        geslaagd: true,
        actie: mfaStep === 'enroll' ? 'mfa_instelling' : 'mfa_verificatie'
      });

      setMfaRequired(false);
      setMfaStep(null);
      setMfaData({});
      setMfaCode('');
      mfaFlowActiveRef.current = false;
    } catch (error) {
      setMfaError('Ongeldige code, probeer opnieuw.');
      securityService.logLoginPoging({
        gebruikersnaam: user?.email,
        geslaagd: false,
        actie: mfaStep === 'enroll' ? 'mfa_instelling' : 'mfa_verificatie',
        foutmelding: 'Ongeldige code'
      });
    }
    setMfaLoading(false);
  };

  // Login Component
  const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [msLoading, setMsLoading] = useState(false);

    const handleLogin = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      try {
        await authService.signInWithUsername(username, password);
        // Geen success log hier: MFA is nog niet geverifieerd.
        // De definitieve inlog wordt gelogd na succesvolle MFA verificatie.
      } catch (error) {
        securityService.logLoginPoging({ gebruikersnaam: username, geslaagd: false, foutmelding: error.message });
        showNotification('Login mislukt: ' + error.message, 'error');
      }
      setIsLoading(false);
    };

    const handleMicrosoftLogin = async () => {
      setMsLoading(true);
      try {
        await authService.signInWithAzure();
      } catch (error) {
        securityService.logLoginPoging({ gebruikersnaam: 'microsoft', geslaagd: false, foutmelding: error.message });
        showNotification('Microsoft login mislukt: ' + error.message, 'error');
        setMsLoading(false);
      }
    };

    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center p-4 font-exo`}>
        <div className={`${t.bgSecondary} rounded-xl shadow-2xl p-8 w-full max-w-md border ${theme === 'dark' ? 'border-zf-accent/20' : 'border-gray-200'}`}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-zf-accent/20 rounded-full mb-4">
              <Car className="text-zf-accent" size={40} />
            </div>
            <h1 className={`text-3xl font-bold ${t.text}`}>ZF-Portaal</h1>
            <p className={`${t.textMuted} mt-2`}>Zomerfeesten de Hoeve</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="login-username" className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Gebruikersnaam</label>
              <input
                id="login-username"
                name="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`w-full px-4 py-3 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                placeholder="gebruikersnaam"
                required
              />
            </div>

            <div>
              <label htmlFor="login-password" className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Wachtwoord</label>
              <input
                id="login-password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-zf-accent text-white py-3 rounded-lg hover:bg-zf-accent/80 transition-colors flex items-center justify-center gap-2 disabled:bg-zf-accent/50 font-semibold"
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Inloggen...
                </>
              ) : (
                'Inloggen'
              )}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className={`flex-1 h-px ${t.border} border-t`} />
            <span className={`text-xs ${t.textMuted}`}>of</span>
            <div className={`flex-1 h-px ${t.border} border-t`} />
          </div>

          <button
            onClick={handleMicrosoftLogin}
            disabled={msLoading}
            className={`mt-4 w-full flex items-center justify-center gap-3 py-3 px-4 border ${t.border} rounded-lg ${t.hover} transition-colors disabled:opacity-50`}
          >
            {msLoading ? (
              <Loader className="animate-spin text-zf-accent" size={20} />
            ) : (
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
            )}
            <span className={t.text}>Inloggen met Microsoft</span>
          </button>
        </div>
      </div>
    );
  };

  // Dashboard Component
  const Dashboard = () => {
    const [stats, setStats] = useState({
      total_vehicles: 0,
      scans_today: 0,
      scans_week: 0,
      scans_month: 0
    });
    const [recentScans, setRecentScans] = useState([]);
    const [dashLoading, setDashLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showDatePicker, setShowDatePicker] = useState(false);

    // Check if viewing today
    const isToday = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);
      return selected.getTime() === today.getTime();
    };

    // Format date for display
    const formatDateDisplay = (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(date);
      selected.setHours(0, 0, 0, 0);

      if (selected.getTime() === today.getTime()) {
        return 'Vandaag';
      }

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (selected.getTime() === yesterday.getTime()) {
        return 'Gisteren';
      }

      return selected.toLocaleDateString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    };

    // Format date for input value (YYYY-MM-DD)
    const formatDateForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const getTodayString = () => formatDateForInput(new Date());

    const handleDateSelect = (dateString) => {
      setSelectedDate(new Date(dateString));
      setShowDatePicker(false);
    };

    const goToToday = () => {
      setSelectedDate(new Date());
      setShowDatePicker(false);
    };

    useEffect(() => {
      const loadDashboardData = async () => {
        setDashLoading(true);
        try {
          const statsData = await statsService.getDashboardStats();
          if (statsData) setStats(statsData);

          // Load scans for selected date
          const scansData = await scanService.getRecent(100);
          if (scansData) {
            // Filter scans by selected date
            const dayStart = new Date(selectedDate);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(selectedDate);
            dayEnd.setHours(23, 59, 59, 999);

            const filteredScans = scansData.filter(scan => {
              const scanTime = new Date(scan.scan_timestamp);
              return scanTime >= dayStart && scanTime <= dayEnd;
            });
            setRecentScans(filteredScans);
          }
        } catch (error) {
          console.error('Error loading dashboard:', error);
          showNotification('Fout bij laden dashboard: ' + error.message, 'error');
        }
        setDashLoading(false);
      };

      loadDashboardData();
    }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

    if (dashLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader className="animate-spin text-zf-accent" size={40} />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className={`text-3xl font-bold ${t.text}`}>Dashboard</h1>
          <button
            onClick={() => setCurrentPage('vehicles')}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors font-medium"
          >
            <Plus size={20} />
            Nieuw Kenteken
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title="Totaal Kentekens" value={stats.total_vehicles} icon={<Car />} color="accent" />
          <StatCard title="Scans Vandaag" value={stats.scans_today} icon={<Activity />} color="green" />
          <StatCard title="Scans Deze Week" value={stats.scans_week} icon={<BarChart3 />} color="blue" />
          <StatCard title="Scans Deze Maand" value={stats.scans_month} icon={<FileText />} color="purple" />
        </div>

        {/* Date Selection */}
        <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${t.text}`}>Scans overzicht</h2>
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${t.border} ${t.hover} transition-colors`}
              >
                <Calendar className={`w-5 h-5 ${t.textSecondary}`} />
                <span className={`font-medium ${t.text}`}>{formatDateDisplay(selectedDate)}</span>
              </button>

              {showDatePicker && (
                <div className={`absolute right-0 top-full mt-2 z-50 ${t.card} rounded-xl shadow-xl border p-4`}>
                  <input
                    type="date"
                    value={formatDateForInput(selectedDate)}
                    max={getTodayString()}
                    onChange={(e) => handleDateSelect(e.target.value)}
                    className={`${t.input} border rounded-lg px-4 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={goToToday}
                      className="flex-1 bg-zf-accent text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-zf-accent/80 transition-colors"
                    >
                      Vandaag
                    </button>
                    <button
                      onClick={() => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        setSelectedDate(yesterday);
                        setShowDatePicker(false);
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${t.border} ${t.text} ${t.hover} transition-colors`}
                    >
                      Gisteren
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-semibold ${t.text}`}>
              Scans {isToday() ? 'Vandaag' : formatDateDisplay(selectedDate)}
            </h2>
            <span className={`${t.textSecondary} text-sm`}>{recentScans.length} scans</span>
          </div>
          {recentScans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${t.border}`}>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Kenteken</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Tijdstip</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentScans.map((scan) => (
                    <tr key={scan.id} className={`border-b ${t.border} ${t.hover}`}>
                      <td className={`py-3 px-4 font-semibold ${t.text}`}>{scan.kenteken}</td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>
                        {new Date(scan.scan_timestamp).toLocaleString('nl-NL')}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          scan.status === 'toegang' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {scan.status === 'toegang' ? 'Toegang' : 'Geweigerd'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={t.textMuted}>Geen scans op deze datum.</p>
          )}
        </div>
      </div>
    );
  };

  // Vehicles Component
  const Vehicles = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [vehicles, setVehicles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [vehicleLoading, setVehicleLoading] = useState(true);
    const [formData, setFormData] = useState({
      kenteken: '',
      organisatie: '',
      voornaam: '',
      achternaam: '',
      telefoonnummer: '',
      toegang: true,
      toegang_type: 'altijd', // 'altijd' of 'datums'
      toegang_datums: [],
      notities: ''
    });
    const [editFormData, setEditFormData] = useState({
      kenteken: '',
      organisatie: '',
      voornaam: '',
      achternaam: '',
      telefoonnummer: '',
      toegang: true,
      toegang_type: 'altijd',
      toegang_datums: [],
      notities: ''
    });
    const [newDatum, setNewDatum] = useState('');
    const [editNewDatum, setEditNewDatum] = useState('');
    const [maxKentekensPerDag, setMaxKentekensPerDag] = useState(0);
    const [evenementDatums, setEvenementDatums] = useState([]);

    useEffect(() => {
      loadVehicles();
      const loadSettings = async () => {
        try {
          const [maxValue, datumsValue] = await Promise.all([
            appSettingsService.getSetting('max_kentekens_per_dag'),
            appSettingsService.getSetting('evenement_datums')
          ]);
          setMaxKentekensPerDag(parseInt(maxValue) || 0);
          setEvenementDatums(datumsValue ? JSON.parse(datumsValue) : []);
        } catch (err) {
          console.error('Error loading kenteken settings:', err);
        }
      };
      loadSettings();
    }, []);

    const loadVehicles = async () => {
      setVehicleLoading(true);
      try {
        const data = await vehicleService.getAll();
        setVehicles(data || []);
      } catch (error) {
        console.error('Error loading vehicles:', error);
        showNotification('Fout bij laden kentekens: ' + error.message, 'error');
      }
      setVehicleLoading(false);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        // Remove toegang_type from data - it's only for UI
        const { toegang_type, ...vehicleData } = formData;
        const dataToSubmit = {
          ...vehicleData,
          toegang_datums: formData.toegang_type === 'datums' ? formData.toegang_datums : []
        };
        await vehicleService.create(dataToSubmit);
        auditService.log({ changedBy: user?.id, action: 'INSERT', tableName: 'vehicles', recordId: dataToSubmit.kenteken, newData: { kenteken: dataToSubmit.kenteken, organisatie: dataToSubmit.organisatie, toegang: dataToSubmit.toegang, door: userProfile?.username } });
        showNotification('Kenteken succesvol toegevoegd!', 'success');
        setShowModal(false);
        setFormData({
          kenteken: '',
          organisatie: '',
          voornaam: '',
          achternaam: '',
          telefoonnummer: '',
          toegang: true,
          toegang_type: 'altijd',
          toegang_datums: [],
          notities: ''
        });
        setNewDatum('');
        loadVehicles();
      } catch (error) {
        showNotification('Fout bij toevoegen: ' + error.message, 'error');
      }
    };

    const addDatum = () => {
      if (newDatum && !formData.toegang_datums.includes(newDatum)) {
        setFormData({
          ...formData,
          toegang_datums: [...formData.toegang_datums, newDatum].sort()
        });
        setNewDatum('');
      }
    };

    const removeDatum = (datum) => {
      setFormData({
        ...formData,
        toegang_datums: formData.toegang_datums.filter(d => d !== datum)
      });
    };

    const addEditDatum = () => {
      if (editNewDatum && !editFormData.toegang_datums.includes(editNewDatum)) {
        setEditFormData({
          ...editFormData,
          toegang_datums: [...editFormData.toegang_datums, editNewDatum].sort()
        });
        setEditNewDatum('');
      }
    };

    const removeEditDatum = (datum) => {
      setEditFormData({
        ...editFormData,
        toegang_datums: editFormData.toegang_datums.filter(d => d !== datum)
      });
    };

    const handleEditVehicle = async (e) => {
      e.preventDefault();
      try {
        // Remove toegang_type from data - it's only for UI
        const { toegang_type, ...vehicleData } = editFormData;
        const dataToSubmit = {
          ...vehicleData,
          toegang_datums: editFormData.toegang_type === 'datums' ? editFormData.toegang_datums : []
        };
        // If kenteken changed, we need to delete old and create new
        if (editFormData.kenteken !== editingVehicle.kenteken) {
          await vehicleService.delete(editingVehicle.kenteken);
          await vehicleService.create(dataToSubmit);
        } else {
          await vehicleService.update(editingVehicle.kenteken, dataToSubmit);
        }
        auditService.log({ changedBy: user?.id, action: 'UPDATE', tableName: 'vehicles', recordId: dataToSubmit.kenteken, oldData: { kenteken: editingVehicle.kenteken }, newData: { kenteken: dataToSubmit.kenteken, organisatie: dataToSubmit.organisatie, toegang: dataToSubmit.toegang, door: userProfile?.username } });
        showNotification('Kenteken succesvol bijgewerkt!', 'success');
        setShowEditModal(false);
        setEditingVehicle(null);
        setEditNewDatum('');
        loadVehicles();
      } catch (error) {
        showNotification('Fout bij bijwerken: ' + error.message, 'error');
      }
    };

    const handleDeleteVehicle = async (kenteken) => {
      if (!window.confirm(`Weet je zeker dat je kenteken ${kenteken} wilt verwijderen?`)) {
        return;
      }
      try {
        await vehicleService.delete(kenteken);
        auditService.log({ changedBy: user?.id, action: 'DELETE', tableName: 'vehicles', recordId: kenteken, oldData: { kenteken, door: userProfile?.username } });
        showNotification('Kenteken succesvol verwijderd!', 'success');
        loadVehicles();
      } catch (error) {
        showNotification('Fout bij verwijderen: ' + error.message, 'error');
      }
    };

    const openEditModal = (vehicle) => {
      setEditingVehicle(vehicle);
      const datums = vehicle.toegang_datums || [];
      setEditFormData({
        kenteken: vehicle.kenteken || '',
        organisatie: vehicle.organisatie || '',
        voornaam: vehicle.voornaam || '',
        achternaam: vehicle.achternaam || '',
        telefoonnummer: vehicle.telefoonnummer || '',
        toegang: vehicle.toegang,
        toegang_type: datums.length > 0 ? 'datums' : 'altijd',
        toegang_datums: datums,
        notities: vehicle.notities || ''
      });
      setEditNewDatum('');
      setShowEditModal(true);
    };

    const formatDatum = (dateStr) => {
      return new Date(dateStr).toLocaleDateString('nl-NL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    };

    const filteredVehicles = vehicles.filter(v =>
      v.kenteken?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.organisatie?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.voornaam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.achternaam?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Bereken kentekens met toegang voor een specifieke datum
    const getKentekensVoorDatum = (datumStr) => {
      return vehicles.filter(v => {
        if (!v.toegang) return false;
        const datums = v.toegang_datums || [];
        // Altijd toegang (lege datums array) of specifieke datum
        return datums.length === 0 || datums.includes(datumStr);
      }).length;
    };

    // Bereken waarschuwingen per evenement-datum
    const datumWaarschuwingen = maxKentekensPerDag > 0
      ? evenementDatums.map(datumStr => {
          const aantal = getKentekensVoorDatum(datumStr);
          const beschikbaar = maxKentekensPerDag - aantal;
          return { datumStr, aantal, beschikbaar };
        }).filter(w => w.beschikbaar <= 5)
      : [];

    return (
      <div className="space-y-6">
        {/* Waarschuwingsbanners per evenement-datum */}
        {datumWaarschuwingen.map(({ datumStr, aantal, beschikbaar }) => {
          const datumLabel = new Date(datumStr).toLocaleDateString('nl-NL', {
            weekday: 'long', day: 'numeric', month: 'long'
          });
          return (
            <div
              key={datumStr}
              className={`rounded-xl p-4 border flex items-center gap-3 ${
                beschikbaar <= 0
                  ? 'bg-red-500/10 border-red-500/30 text-red-500'
                  : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-500'
              }`}
            >
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <div>
                <p className="font-semibold">
                  {beschikbaar <= 0
                    ? `Maximum aantal kentekens bereikt voor ${datumLabel} (${aantal}/${maxKentekensPerDag})`
                    : `Let op: nog ${beschikbaar} kenteken${beschikbaar !== 1 ? 's' : ''} beschikbaar voor ${datumLabel} (${aantal}/${maxKentekensPerDag})`
                  }
                </p>
              </div>
            </div>
          );
        })}

        <div className="flex justify-between items-center">
          <h1 className={`text-3xl font-bold ${t.text}`}>Kenteken Beheer</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open('/scanner', '_blank')}
              className={`${t.card} border px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/10 transition-colors font-medium ${t.textSecondary}`}
            >
              <Camera size={20} />
              Web Scanner
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors font-medium"
            >
              <Plus size={20} />
              Kenteken Toevoegen
            </button>
          </div>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
          <div className="relative">
            <Search className={`absolute left-3 top-3 ${t.textMuted}`} size={20} />
            <input
              type="text"
              id="kenteken-zoek"
              name="kenteken-zoek"
              placeholder="Zoek op kenteken, organisatie of naam..."
              className={`w-full pl-10 pr-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {vehicleLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-zf-accent" size={32} />
            </div>
          ) : filteredVehicles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${t.border}`}>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Kenteken</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Organisatie</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Naam</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Telefoon</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Toegang</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Datums</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Notities</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((vehicle) => (
                    <tr key={vehicle.kenteken} className={`border-b ${t.border} ${t.hover}`}>
                      <td className={`py-3 px-4 font-semibold ${t.text}`}>{vehicle.kenteken}</td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>{vehicle.organisatie || '-'}</td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>{vehicle.voornaam} {vehicle.achternaam}</td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>{vehicle.telefoonnummer || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          vehicle.toegang ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {vehicle.toegang ? 'Ja' : 'Nee'}
                        </span>
                      </td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>
                        {vehicle.toegang_datums && vehicle.toegang_datums.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {vehicle.toegang_datums.map((datum, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                {formatDatum(datum)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs">Altijd</span>
                        )}
                      </td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>{vehicle.notities || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(vehicle)}
                            className="text-zf-accent hover:text-zf-accent/80 p-1"
                            title="Bewerken"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteVehicle(vehicle.kenteken)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Verwijderen"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={`${t.textMuted} text-center py-8`}>
              {searchTerm ? 'Geen kentekens gevonden.' : 'Nog geen kentekens. Voeg je eerste kenteken toe!'}
            </p>
          )}
        </div>

        {/* Add Vehicle Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className={`${t.card} rounded-xl p-6 w-full max-w-md border`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold ${t.text}`}>Kenteken Toevoegen</h2>
                <button onClick={() => setShowModal(false)} className={`${t.textMuted} hover:${t.text}`}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Kenteken *</label>
                  <input
                    type="text"
                    value={formData.kenteken}
                    onChange={(e) => setFormData({...formData, kenteken: e.target.value.toUpperCase()})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="XX-XX-XX"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Organisatie</label>
                  <input
                    type="text"
                    value={formData.organisatie}
                    onChange={(e) => setFormData({...formData, organisatie: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Bedrijfsnaam"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Voornaam *</label>
                    <input
                      type="text"
                      value={formData.voornaam}
                      onChange={(e) => setFormData({...formData, voornaam: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      placeholder="Voornaam"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Achternaam *</label>
                    <input
                      type="text"
                      value={formData.achternaam}
                      onChange={(e) => setFormData({...formData, achternaam: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      placeholder="Achternaam"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Telefoonnummer</label>
                  <input
                    type="tel"
                    value={formData.telefoonnummer}
                    onChange={(e) => setFormData({...formData, telefoonnummer: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="0612345678"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Toegang</label>
                  <select
                    value={formData.toegang ? 'true' : 'false'}
                    onChange={(e) => setFormData({...formData, toegang: e.target.value === 'true'})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                  >
                    <option value="true">Toegestaan</option>
                    <option value="false">Geweigerd</option>
                  </select>
                </div>

                {formData.toegang && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Toegang periode</label>
                    <select
                      value={formData.toegang_type}
                      onChange={(e) => setFormData({...formData, toegang_type: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    >
                      <option value="altijd">Altijd toegang</option>
                      <option value="datums">Specifieke datums</option>
                    </select>
                  </div>
                )}

                {formData.toegang && formData.toegang_type === 'datums' && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Goedgekeurde datums</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="date"
                        value={newDatum}
                        onChange={(e) => setNewDatum(e.target.value)}
                        className={`flex-1 px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      />
                      <button
                        type="button"
                        onClick={addDatum}
                        className="px-4 py-2 bg-zf-accent text-white rounded-lg hover:bg-zf-accent/80 transition-colors"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                    {formData.toegang_datums.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.toegang_datums.map((datum, idx) => (
                          <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm">
                            {formatDatum(datum)}
                            <button
                              type="button"
                              onClick={() => removeDatum(datum)}
                              className="hover:text-red-400"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Notities</label>
                  <textarea
                    value={formData.notities}
                    onChange={(e) => setFormData({...formData, notities: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Extra informatie..."
                    rows="3"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className={`flex-1 px-4 py-2 border ${t.border} rounded-lg ${t.hover} transition-colors ${t.textSecondary}`}
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors font-medium"
                  >
                    Toevoegen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Vehicle Modal */}
        {showEditModal && editingVehicle && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto py-4">
            <div className={`${t.card} rounded-xl p-6 w-full max-w-md border my-auto`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold ${t.text}`}>Kenteken Bewerken</h2>
                <button onClick={() => setShowEditModal(false)} className={`${t.textMuted} hover:${t.text}`}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleEditVehicle} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Kenteken *</label>
                  <input
                    type="text"
                    value={editFormData.kenteken}
                    onChange={(e) => setEditFormData({...editFormData, kenteken: e.target.value.toUpperCase()})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="XX-XX-XX"
                    required
                  />
                  {editFormData.kenteken !== editingVehicle.kenteken && (
                    <p className="text-xs text-yellow-500 mt-1">Let op: kenteken wijzigen verwijdert het oude record en maakt een nieuw aan.</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Organisatie</label>
                  <input
                    type="text"
                    value={editFormData.organisatie}
                    onChange={(e) => setEditFormData({...editFormData, organisatie: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Bedrijfsnaam"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Voornaam *</label>
                    <input
                      type="text"
                      value={editFormData.voornaam}
                      onChange={(e) => setEditFormData({...editFormData, voornaam: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      placeholder="Voornaam"
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Achternaam *</label>
                    <input
                      type="text"
                      value={editFormData.achternaam}
                      onChange={(e) => setEditFormData({...editFormData, achternaam: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      placeholder="Achternaam"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Telefoonnummer</label>
                  <input
                    type="tel"
                    value={editFormData.telefoonnummer}
                    onChange={(e) => setEditFormData({...editFormData, telefoonnummer: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="0612345678"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Toegang</label>
                  <select
                    value={editFormData.toegang ? 'true' : 'false'}
                    onChange={(e) => setEditFormData({...editFormData, toegang: e.target.value === 'true'})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                  >
                    <option value="true">Toegestaan</option>
                    <option value="false">Geweigerd</option>
                  </select>
                </div>

                {editFormData.toegang && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Toegang periode</label>
                    <select
                      value={editFormData.toegang_type}
                      onChange={(e) => setEditFormData({...editFormData, toegang_type: e.target.value})}
                      className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    >
                      <option value="altijd">Altijd toegang</option>
                      <option value="datums">Specifieke datums</option>
                    </select>
                  </div>
                )}

                {editFormData.toegang && editFormData.toegang_type === 'datums' && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Goedgekeurde datums</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="date"
                        value={editNewDatum}
                        onChange={(e) => setEditNewDatum(e.target.value)}
                        className={`flex-1 px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                      />
                      <button
                        type="button"
                        onClick={addEditDatum}
                        className="px-4 py-2 bg-zf-accent text-white rounded-lg hover:bg-zf-accent/80 transition-colors"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                    {editFormData.toegang_datums.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {editFormData.toegang_datums.map((datum, idx) => (
                          <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm">
                            {formatDatum(datum)}
                            <button
                              type="button"
                              onClick={() => removeEditDatum(datum)}
                              className="hover:text-red-400"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Notities</label>
                  <textarea
                    value={editFormData.notities}
                    onChange={(e) => setEditFormData({...editFormData, notities: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Extra informatie..."
                    rows="3"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className={`flex-1 px-4 py-2 border ${t.border} rounded-lg ${t.hover} transition-colors ${t.textSecondary}`}
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors font-medium"
                  >
                    Opslaan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Security Log Component (Admin Only)
  const SecurityLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadLogs = async () => {
        setLoading(true);
        try {
          const data = await securityService.getLogs(200);
          setLogs(data);
        } catch (error) {
          showNotification('Fout bij laden security log: ' + error.message, 'error');
        }
        setLoading(false);
      };
      loadLogs();
    }, []);

    return (
      <div className="space-y-6">
        <h1 className={`text-3xl font-bold ${t.text}`}>Security Log</h1>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-zf-accent" size={32} />
            </div>
          ) : logs.length === 0 ? (
            <p className={t.textMuted}>Geen inlogpogingen gevonden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${t.border}`}>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Tijdstip</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Gebruikersnaam</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>IP-adres</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Browser</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Status</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Foutmelding</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className={`border-b ${t.border} ${t.hover}`}>
                      <td className={`py-3 px-4 ${t.textSecondary} whitespace-nowrap`}>
                        {new Date(log.tijdstip).toLocaleString('nl-NL')}
                      </td>
                      <td className={`py-3 px-4 font-semibold ${t.text}`}>{log.gebruikersnaam || '-'}</td>
                      <td className={`py-3 px-4 ${t.textSecondary} font-mono`}>{log.ip_adres || '-'}</td>
                      <td className={`py-3 px-4 ${t.textSecondary} max-w-xs truncate`} title={log.browser}>{log.browser || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          log.geslaagd ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {log.geslaagd ? 'Geslaagd' : 'Mislukt'}
                        </span>
                      </td>
                      <td className={`py-3 px-4 ${t.textSecondary} max-w-xs truncate`} title={log.foutmelding}>{log.foutmelding || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Users Management Component (Admin Only)
  const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [createFormData, setCreateFormData] = useState({
      email: '',
      password: '',
      username: '',
      role: 'gebruiker',
      allowed_pages: [...DEFAULT_ALLOWED_PAGES]
    });
    const [editFormData, setEditFormData] = useState({
      role: 'gebruiker',
      password: '',
      allowed_pages: [...DEFAULT_ALLOWED_PAGES]
    });

    useEffect(() => {
      loadUsers();
    }, []);

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const data = await userService.getAllUsers();
        setUsers(data || []);
      } catch (error) {
        console.error('Error loading users:', error);
        showNotification('Fout bij laden gebruikers: ' + error.message, 'error');
      }
      setUsersLoading(false);
    };

    const handleCreateUser = async (e) => {
      e.preventDefault();
      try {
        await userService.createUser(
          createFormData.email,
          createFormData.password,
          createFormData.role,
          createFormData.username,
          createFormData.role === 'admin' ? DEFAULT_ALLOWED_PAGES : createFormData.allowed_pages
        );
        auditService.log({ changedBy: user?.id, action: 'INSERT', tableName: 'user_profiles', recordId: createFormData.username, newData: { email: createFormData.email, rol: createFormData.role, door: userProfile?.username } });
        showNotification('Gebruiker succesvol aangemaakt!', 'success');
        setShowCreateModal(false);
        setCreateFormData({ email: '', password: '', username: '', role: 'gebruiker', allowed_pages: [...DEFAULT_ALLOWED_PAGES] });
        loadUsers();
      } catch (error) {
        showNotification('Fout bij aanmaken gebruiker: ' + error.message, 'error');
      }
    };

    const handleEditUser = async (e) => {
      e.preventDefault();
      if (editFormData.role !== 'admin' && editFormData.allowed_pages.length === 0) {
        showNotification('Selecteer minimaal één pagina', 'error');
        return;
      }
      try {
        await userService.updateUserProfile(editingUser.id, {
          role: editFormData.role,
          allowed_pages: editFormData.role === 'admin' ? DEFAULT_ALLOWED_PAGES : editFormData.allowed_pages
        });

        if (editFormData.password && editFormData.password.trim() !== '') {
          await userService.updateUserPassword(editingUser.id, editFormData.password);
        }

        auditService.log({ changedBy: user?.id, action: 'UPDATE', tableName: 'user_profiles', recordId: editingUser.id, newData: { rol: editFormData.role, wachtwoord_gewijzigd: !!(editFormData.password && editFormData.password.trim()), door: userProfile?.username } });
        showNotification('Gebruiker succesvol bijgewerkt!', 'success');
        setShowEditModal(false);
        setEditingUser(null);
        loadUsers();
      } catch (error) {
        showNotification('Fout bij bijwerken gebruiker: ' + error.message, 'error');
      }
    };

    const handleUnlockUser = async (userId, username) => {
      try {
        await userService.unlockUser(userId);
        auditService.log({ changedBy: user?.id, action: 'UPDATE', tableName: 'user_profiles', recordId: userId, newData: { actie: 'account_ontgrendeld', door: userProfile?.username, gebruiker: username } });
        showNotification(`Account ${username} ontgrendeld.`, 'success');
        loadUsers();
      } catch (error) {
        showNotification('Fout bij ontgrendelen: ' + error.message, 'error');
      }
    };

    const handleDeleteUser = async (userId, userEmail) => {
      if (!window.confirm(`Weet je zeker dat je gebruiker ${userEmail} wilt verwijderen?`)) {
        return;
      }

      try {
        await userService.deleteUser(userId);
        auditService.log({ changedBy: user?.id, action: 'DELETE', tableName: 'user_profiles', recordId: userId, oldData: { email: userEmail, door: userProfile?.username } });
        showNotification('Gebruiker succesvol verwijderd!', 'success');
        loadUsers();
      } catch (error) {
        showNotification('Fout bij verwijderen gebruiker: ' + error.message, 'error');
      }
    };

    const openEditModal = (user) => {
      setEditingUser(user);
      setEditFormData({
        role: user.role || 'gebruiker',
        password: '',
        allowed_pages: user.allowed_pages || [...DEFAULT_ALLOWED_PAGES]
      });
      setShowEditModal(true);
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className={`text-3xl font-bold ${t.text}`}>Gebruikers Beheer</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors font-medium"
          >
            <Plus size={20} />
            Gebruiker Toevoegen
          </button>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {usersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-zf-accent" size={32} />
            </div>
          ) : users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${t.border}`}>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Gebruikersnaam</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Email</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Rol</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Toegang</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Status</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Laatste Login</th>
                    <th className={`text-left py-3 px-4 ${t.textSecondary} font-semibold`}>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userItem) => (
                    <tr key={userItem.id} className={`border-b ${t.border} ${t.hover}`}>
                      <td className={`py-3 px-4 font-semibold ${t.text}`}>{userItem.username || '-'}</td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>{userItem.email || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          userItem.role === 'admin' ? 'bg-zf-accent/20 text-zf-accent' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {userItem.role === 'admin' ? 'admin' : 'gebruiker'}
                        </span>
                      </td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>
                        {userItem.role === 'admin'
                          ? <span className="text-xs text-zf-accent">Alle pagina's</span>
                          : <span className="text-xs">{(userItem.allowed_pages || []).length}/{ALL_MAIN_PAGES.length} pagina's</span>
                        }
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                            userItem.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {userItem.active ? 'Actief' : 'Inactief'}
                          </span>
                          {userItem.locked_until && new Date(userItem.locked_until) > new Date() && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium w-fit bg-orange-500/20 text-orange-400" title={`Geblokkeerd tot ${new Date(userItem.locked_until).toLocaleString('nl-NL')}`}>
                              Geblokkeerd
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`py-3 px-4 ${t.textSecondary}`}>
                        {userItem.last_login ? new Date(userItem.last_login).toLocaleDateString('nl-NL') : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(userItem)}
                            className="text-zf-accent hover:text-zf-accent/80 p-1"
                            title="Bewerken"
                          >
                            <Edit2 size={18} />
                          </button>
                          {userItem.locked_until && new Date(userItem.locked_until) > new Date() && (
                            <button
                              onClick={() => handleUnlockUser(userItem.id, userItem.username)}
                              className="text-orange-400 hover:text-orange-300 p-1"
                              title="Ontgrendelen"
                            >
                              <LockOpen size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(userItem.id, userItem.username)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Verwijderen"
                            disabled={userItem.id === user?.id}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={`${t.textMuted} text-center py-8`}>Geen gebruikers gevonden.</p>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto py-4">
            <div className={`${t.card} rounded-xl p-6 w-full max-w-md border my-auto`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold ${t.text}`}>Gebruiker Toevoegen</h2>
                <button onClick={() => setShowCreateModal(false)} className={`${t.textMuted} hover:${t.text}`}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Email *</label>
                  <input
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({...createFormData, email: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="gebruiker@email.nl"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Gebruikersnaam</label>
                  <input
                    type="text"
                    value={createFormData.username}
                    onChange={(e) => setCreateFormData({...createFormData, username: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Laat leeg voor automatisch"
                  />
                  <p className={`text-xs ${t.textMuted} mt-1`}>Optioneel - wordt automatisch van email gegenereerd als leeg</p>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Wachtwoord *</label>
                  <input
                    type="password"
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData({...createFormData, password: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Minimaal 10 tekens"
                    minLength="10"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Rol *</label>
                  <select
                    value={createFormData.role}
                    onChange={(e) => setCreateFormData({...createFormData, role: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                  >
                    <option value="gebruiker">Gebruiker</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                {createFormData.role !== 'admin' && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Pagina Toegang</label>
                    <p className={`text-xs ${t.textMuted} mb-3`}>Selecteer welke pagina's deze gebruiker mag zien</p>
                    <div className="space-y-2">
                      {ALL_MAIN_PAGES.map(page => (
                        <label key={page.key} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${t.hover} cursor-pointer`}>
                          <input
                            type="checkbox"
                            checked={createFormData.allowed_pages.includes(page.key)}
                            onChange={(e) => {
                              const pages = e.target.checked
                                ? [...createFormData.allowed_pages, page.key]
                                : createFormData.allowed_pages.filter(p => p !== page.key);
                              setCreateFormData({...createFormData, allowed_pages: pages});
                            }}
                            className="w-4 h-4 text-zf-accent bg-transparent border-gray-500 rounded focus:ring-zf-accent"
                          />
                          <span className={`text-sm ${t.text}`}>{page.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className={`flex-1 px-4 py-2 border ${t.border} rounded-lg ${t.hover} transition-colors ${t.textSecondary}`}
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors font-medium"
                  >
                    Aanmaken
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto py-4">
            <div className={`${t.card} rounded-xl p-6 w-full max-w-md border my-auto`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold ${t.text}`}>Gebruiker Bewerken</h2>
                <button onClick={() => setShowEditModal(false)} className={`${t.textMuted} hover:${t.text}`}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleEditUser} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Gebruikersnaam</label>
                  <input
                    type="text"
                    value={editingUser.username}
                    disabled
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg ${t.textMuted} cursor-not-allowed`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Email</label>
                  <input
                    type="email"
                    value={editingUser.email || ''}
                    disabled
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg ${t.textMuted} cursor-not-allowed`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Nieuw Wachtwoord</label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({...editFormData, password: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                    placeholder="Laat leeg om ongewijzigd te laten"
                    minLength="10"
                  />
                  <p className={`text-xs ${t.textMuted} mt-1`}>Minimaal 10 tekens. Laat leeg om wachtwoord niet te wijzigen.</p>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Rol *</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                    className={`w-full px-4 py-2 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                  >
                    <option value="gebruiker">Gebruiker</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                {editFormData.role !== 'admin' && (
                  <div>
                    <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>Pagina Toegang</label>
                    <p className={`text-xs ${t.textMuted} mb-3`}>Selecteer welke pagina's deze gebruiker mag zien</p>
                    <div className="space-y-2">
                      {ALL_MAIN_PAGES.map(page => (
                        <label key={page.key} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${t.hover} cursor-pointer`}>
                          <input
                            type="checkbox"
                            checked={editFormData.allowed_pages.includes(page.key)}
                            onChange={(e) => {
                              const pages = e.target.checked
                                ? [...editFormData.allowed_pages, page.key]
                                : editFormData.allowed_pages.filter(p => p !== page.key);
                              setEditFormData({...editFormData, allowed_pages: pages});
                            }}
                            className="w-4 h-4 text-zf-accent bg-transparent border-gray-500 rounded focus:ring-zf-accent"
                          />
                          <span className={`text-sm ${t.text}`}>{page.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className={`flex-1 px-4 py-2 border ${t.border} rounded-lg ${t.hover} transition-colors ${t.textSecondary}`}
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors font-medium"
                  >
                    Opslaan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Settings Component
  const SettingsPage = () => {
    return (
      <div className="space-y-6">
        <h1 className={`text-3xl font-bold ${t.text}`}>Instellingen</h1>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Weergave</h2>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${t.text}`}>Thema</p>
              <p className={`text-sm ${t.textMuted}`}>Kies tussen donker en licht thema</p>
            </div>
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-3 px-4 py-2 ${t.card} border ${t.border} rounded-lg ${t.hover} transition-colors`}
            >
              {theme === 'dark' ? (
                <>
                  <Moon className="text-zf-accent" size={20} />
                  <span className={t.text}>Donker</span>
                </>
              ) : (
                <>
                  <Sun className="text-yellow-500" size={20} />
                  <span className={t.text}>Licht</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Account</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${t.text}`}>Gebruikersnaam</p>
                <p className={`text-sm ${t.textMuted}`}>{userProfile?.username || '-'}</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${t.text}`}>Rol</p>
                <p className={`text-sm ${t.textMuted}`}>{userProfile?.role === 'admin' ? 'Administrator' : 'Gebruiker'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Over</h2>

          <div className="space-y-2">
            <p className={t.textSecondary}>ZF Kenteken Scanner</p>
            <p className={`text-sm ${t.textMuted}`}>Versie 1.0.0</p>
            <p className={`text-sm ${t.textMuted}`}>Zomerfeesten de Hoeve</p>
          </div>
        </div>
      </div>
    );
  };

  // Stat Card Component
  const StatCard = ({ title, value, icon, color }) => {
    const colors = {
      accent: 'bg-zf-accent/20 text-zf-accent',
      green: 'bg-green-500/20 text-green-400',
      blue: 'bg-blue-500/20 text-blue-400',
      purple: 'bg-purple-500/20 text-purple-400'
    };

    return (
      <div className={`${t.card} rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`${t.textMuted} text-sm font-medium`}>{title}</p>
            <p className={`text-3xl font-bold ${t.text} mt-2`}>{value}</p>
          </div>
          <div className={`p-3 rounded-full ${colors[color]}`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  // Navigation Item Component
  const NavItem = ({ icon, label, active, onClick, collapsed }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
        active ? `${t.navActive} border-l-4 border-zf-accent` : ''
      } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
    >
      <span className={active ? 'text-zf-accent' : t.textMuted}>{icon}</span>
      {!collapsed && <span className={t.text}>{label}</span>}
    </button>
  );

  // Page wrapper: keeps component mounted but hidden when not active
  const PageWrapper = ({ page, children }) => (
    <div style={{ display: currentPage === page ? 'block' : 'none' }}>
      {children}
    </div>
  );

  // Check if current user is admin
  const isAdmin = userProfile?.role === 'admin';
  const canAccessPage = (pageKey) => hasPageAccess(userProfile, pageKey);

  // MFA laadscherm terwijl flow wordt gestart (vóór de userProfile check)
  if (mfaRequired && !mfaStep) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center font-exo`}>
        <div className="flex flex-col items-center gap-4">
          <Loader className="animate-spin text-zf-accent" size={40} />
          <p className={t.textMuted}>MFA verificatie laden...</p>
        </div>
      </div>
    );
  }

  // MFA foutscherm (vóór de userProfile check)
  if (mfaRequired && mfaStep === 'error') {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center p-4 font-exo`}>
        <div className={`${t.bgSecondary} rounded-xl shadow-2xl p-8 w-full max-w-md border border-red-500/30 text-center`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/20 rounded-full mb-4">
            <AlertCircle className="text-red-400" size={40} />
          </div>
          <h1 className={`text-2xl font-bold ${t.text} mb-2`}>MFA instellen mislukt</h1>
          <p className={`${t.textMuted} mb-3`}>
            Er staat nog een eerdere, niet-voltooide MFA-aanvraag open. Neem contact op met een beheerder om de MFA-factors te verwijderen via de Supabase-beheeromgeving.
          </p>
          <p className={`text-xs ${t.textMuted} mb-6 font-mono bg-black/20 rounded px-3 py-2`}>
            Authentication → Users → [gebruiker] → MFA factors → verwijder alle factors
          </p>
          <button
            onClick={handleLogout}
            className="w-full bg-zf-accent text-white py-3 rounded-lg hover:bg-zf-accent/80 transition-colors font-semibold"
          >
            Terug naar inloggen
          </button>
        </div>
      </div>
    );
  }

  // MFA scherm (vóór de userProfile check)
  if (mfaRequired && mfaStep) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center p-4 font-exo`}>
        <div className={`${t.bgSecondary} rounded-xl shadow-2xl p-8 w-full max-w-md border ${theme === 'dark' ? 'border-zf-accent/20' : 'border-gray-200'}`}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-zf-accent/20 rounded-full mb-4">
              <Shield className="text-zf-accent" size={40} />
            </div>
            <h1 className={`text-2xl font-bold ${t.text}`}>
              {mfaStep === 'enroll' ? 'MFA Instellen' : 'MFA Verificatie'}
            </h1>
            <p className={`${t.textMuted} mt-2`}>
              {mfaStep === 'enroll'
                ? 'Scan de QR-code met je authenticator app (bijv. Microsoft Authenticator of Google Authenticator)'
                : 'Voer de 6-cijferige code in uit je authenticator app'}
            </p>
          </div>

          {mfaStep === 'enroll' && mfaData.qrCode && (
            <div className="flex flex-col items-center mb-6">
              <div className="bg-white p-3 rounded-lg mb-3">
                <img src={mfaData.qrCode} alt="MFA QR-code" className="w-48 h-48" />
              </div>
              <button
                onClick={() => setMfaShowSecret(!mfaShowSecret)}
                className={`flex items-center gap-2 text-sm ${t.textMuted} hover:text-zf-accent transition-colors`}
              >
                {mfaShowSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                {mfaShowSecret ? 'Verberg sleutel' : 'Toon handmatige invoersleutel'}
              </button>
              {mfaShowSecret && (
                <div className={`mt-2 flex items-center gap-2 px-3 py-2 ${t.card} border ${t.border} rounded-lg`}>
                  <code className={`text-sm font-mono ${t.text} break-all`}>{mfaData.secret}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(mfaData.secret)}
                    className="text-zf-accent hover:text-zf-accent/80 flex-shrink-0"
                    title="Kopiëren"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleMfaSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${t.textSecondary} mb-2`}>
                6-cijferige code
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`w-full px-4 py-3 ${t.input} border rounded-lg focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none text-center text-2xl tracking-widest font-mono`}
                placeholder="000000"
                maxLength={6}
                required
                autoComplete="one-time-code"
              />
            </div>

            {mfaError && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                {mfaError}
              </div>
            )}

            <button
              type="submit"
              disabled={mfaLoading || mfaCode.length !== 6}
              className="w-full bg-zf-accent text-white py-3 rounded-lg hover:bg-zf-accent/80 transition-colors flex items-center justify-center gap-2 disabled:bg-zf-accent/50 font-semibold"
            >
              {mfaLoading ? (
                <><Loader className="animate-spin" size={20} /> Verifiëren...</>
              ) : (
                mfaStep === 'enroll' ? 'MFA Activeren' : 'Verifiëren'
              )}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className={`w-full py-2 text-sm ${t.textMuted} hover:text-zf-accent transition-colors`}
            >
              Terug naar inloggen
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Show loading spinner while checking authentication or loading profile
  if (loading || (user && !userProfile)) {
    return (
      <div className={`flex items-center justify-center h-screen ${t.bg} font-exo`}>
        <Loader className="animate-spin text-zf-accent" size={48} />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login />;
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, t }}>
      <div className={`flex h-screen ${t.bg} font-exo`}>
        {/* Notification Toast */}
        {notification && (
          <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center gap-2`}>
            {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            {notification.message}
          </div>
        )}

        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} ${t.bgSecondary} transition-all duration-300 flex flex-col border-r ${t.border}`}>
          <div className={`p-4 flex items-center justify-between border-b ${t.border}`}>
            {sidebarOpen && <h1 className="text-xl font-bold text-zf-accent">ZF Portaal</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`${t.text} hover:text-zf-accent`}>
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <nav className="flex-1 mt-6">
            {canAccessPage('dashboard') && (
              <NavItem
                icon={<BarChart3 />}
                label="Overzicht"
                active={currentPage === 'dashboard'}
                onClick={() => setCurrentPage('dashboard')}
                collapsed={!sidebarOpen}
              />
            )}
            {canAccessPage('kenteken_scans') && (
              <NavItem
                icon={<Activity />}
                label="Kenteken Scans"
                active={currentPage === 'kenteken_scans'}
                onClick={() => setCurrentPage('kenteken_scans')}
                collapsed={!sidebarOpen}
              />
            )}
            {canAccessPage('vehicles') && (
              <NavItem
                icon={<Car />}
                label="Kentekens"
                active={currentPage === 'vehicles'}
                onClick={() => setCurrentPage('vehicles')}
                collapsed={!sidebarOpen}
              />
            )}
            {canAccessPage('people_counting') && (
              <NavItem
                icon={<Users />}
                label="Personen Telling"
                active={currentPage === 'people_counting'}
                onClick={() => setCurrentPage('people_counting')}
                collapsed={!sidebarOpen}
              />
            )}
            {canAccessPage('mijnetickets') && (
              <NavItem
                icon={<Ticket />}
                label="Mijnetickets"
                active={currentPage === 'mijnetickets'}
                onClick={() => setCurrentPage('mijnetickets')}
                collapsed={!sidebarOpen}
              />
            )}
            {/* Expandable Settings Menu */}
            <div>
              <button
                onClick={() => setSettingsExpanded(!settingsExpanded)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 transition-colors ${
                  currentPage.startsWith('settings_') || currentPage === 'users' || currentPage === 'security_log' ? `${t.navActive} border-l-4 border-zf-accent` : ''
                } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-3">
                  <span className={currentPage.startsWith('settings_') || currentPage === 'users' || currentPage === 'security_log' ? 'text-zf-accent' : t.textMuted}>
                    <Settings />
                  </span>
                  {sidebarOpen && <span className={t.text}>Instellingen</span>}
                </div>
                {sidebarOpen && (
                  <ChevronDown
                    className={`${t.textMuted} transition-transform ${settingsExpanded ? 'rotate-180' : ''}`}
                    size={16}
                  />
                )}
              </button>

              {/* Settings Submenu */}
              {settingsExpanded && sidebarOpen && (
                <div className={`${theme === 'dark' ? 'bg-zf-darker/50' : 'bg-gray-50'}`}>
                  <button
                    onClick={() => setCurrentPage('settings_general')}
                    className={`w-full flex items-center gap-3 pl-12 pr-4 py-2 transition-colors ${
                      currentPage === 'settings_general' ? 'text-zf-accent' : t.textMuted
                    } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
                  >
                    <Sun size={16} />
                    <span>Algemeen</span>
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setCurrentPage('users')}
                        className={`w-full flex items-center gap-3 pl-12 pr-4 py-2 transition-colors ${
                          currentPage === 'users' ? 'text-zf-accent' : t.textMuted
                        } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
                      >
                        <Users size={16} />
                        <span>Gebruikers</span>
                      </button>
                      <button
                        onClick={() => setCurrentPage('settings_kentekens')}
                        className={`w-full flex items-center gap-3 pl-12 pr-4 py-2 transition-colors ${
                          currentPage === 'settings_kentekens' ? 'text-zf-accent' : t.textMuted
                        } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
                      >
                        <Car size={16} />
                        <span>Kentekens</span>
                      </button>
                      <button
                        onClick={() => setCurrentPage('settings_camera')}
                        className={`w-full flex items-center gap-3 pl-12 pr-4 py-2 transition-colors ${
                          currentPage === 'settings_camera' ? 'text-zf-accent' : t.textMuted
                        } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
                      >
                        <Camera size={16} />
                        <span>Camera</span>
                      </button>
                      <button
                        onClick={() => setCurrentPage('security_log')}
                        className={`w-full flex items-center gap-3 pl-12 pr-4 py-2 transition-colors ${
                          currentPage === 'security_log' ? 'text-zf-accent' : t.textMuted
                        } ${theme === 'dark' ? 'hover:bg-zf-dark' : 'hover:bg-gray-100'}`}
                      >
                        <FileText size={16} />
                        <span>Security Log</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </nav>

          <div className={`p-4 border-t ${t.border}`}>
            <NavItem
              icon={<LogOut />}
              label="Uitloggen"
              collapsed={!sidebarOpen}
              onClick={handleLogout}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-8">
            {canAccessPage('dashboard') && (
              <PageWrapper page="dashboard">
                <OverzichtDashboard showNotification={showNotification} isActive={currentPage === 'dashboard'} />
              </PageWrapper>
            )}
            {visitedPages.has('kenteken_scans') && canAccessPage('kenteken_scans') && (
              <PageWrapper page="kenteken_scans">
                <Dashboard />
              </PageWrapper>
            )}
            {visitedPages.has('vehicles') && canAccessPage('vehicles') && (
              <PageWrapper page="vehicles">
                <Vehicles />
              </PageWrapper>
            )}
            {visitedPages.has('mijnetickets') && canAccessPage('mijnetickets') && (
              <PageWrapper page="mijnetickets">
                <MijneticketsDashboard showNotification={showNotification} isActive={currentPage === 'mijnetickets'} />
              </PageWrapper>
            )}
            {visitedPages.has('people_counting') && canAccessPage('people_counting') && (
              <PageWrapper page="people_counting">
                <PeopleCountingDashboard showNotification={showNotification} isActive={currentPage === 'people_counting'} />
              </PageWrapper>
            )}
            {visitedPages.has('users') && (
              <PageWrapper page="users">
                <UsersManagement />
              </PageWrapper>
            )}
            {visitedPages.has('settings_general') && (
              <PageWrapper page="settings_general">
                <SettingsPage />
              </PageWrapper>
            )}
            {visitedPages.has('settings_kentekens') && isAdmin && (
              <PageWrapper page="settings_kentekens">
                <KentekenInstellingen showNotification={showNotification} user={user} userProfile={userProfile} />
              </PageWrapper>
            )}
            {visitedPages.has('settings_camera') && isAdmin && (
              <PageWrapper page="settings_camera">
                <PeopleCountingInstellingen showNotification={showNotification} />
              </PageWrapper>
            )}
            {visitedPages.has('security_log') && isAdmin && (
              <PageWrapper page="security_log">
                <SecurityLog />
              </PageWrapper>
            )}
          </div>
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

export default App;
